import type { Game } from "../game/Game";
import { holdWillDecide } from "./holdWillBot";
import { learnerDecide } from "./learnerBot";
import { randomDecide } from "./randomBot";
import type { BotAction, BotId } from "./types";
import { BOT_LABELS } from "./types";

export type { BotAction, BotId } from "./types";
export { BOT_LABELS };

export interface BotController {
  id: BotId;
  /** Call each frame with dt; returns last action (including waits). */
  tick(game: Game, dt: number): BotAction | null;
  reset(): void;
  /** Last human-readable reason */
  lastReason: string;
}

/**
 * Human-paced controller: think interval, read delay after Reveal,
 * brief reaction lag after a proclaim finishes.
 */
export function createBotController(id: BotId): BotController {
  let thinkAccum = 0;
  let readCooldown = 0;
  let reactionCooldown = 0;
  let wasProclaiming = false;
  let lastReason = "";

  // hold-will: quick competent. learner: slower, studies cards.
  // random: slightly sluggish + wasteful plays.
  const THINK =
    id === "random" ? 0.4 : id === "learner" ? 0.72 : 0.38;
  const READ_TIME =
    id === "learner" ? 1.1 : 0.7;
  const REACT_AFTER_SPEECH =
    id === "random" ? 0.2 : id === "learner" ? 0.48 : 0.22;

  return {
    id,
    get lastReason() {
      return lastReason;
    },
    reset() {
      thinkAccum = 0;
      readCooldown = 0;
      reactionCooldown = 0;
      wasProclaiming = false;
      lastReason = "";
    },
    tick(game: Game, dt: number): BotAction | null {
      if (id === "off") return null;

      if (game.isProclaiming()) {
        wasProclaiming = true;
      } else if (wasProclaiming) {
        wasProclaiming = false;
        reactionCooldown = REACT_AFTER_SPEECH;
      }

      if (readCooldown > 0) {
        readCooldown = Math.max(0, readCooldown - dt);
        // Close reveal after reading so Easy unpauses
        if (readCooldown <= 0 && game.pendingRevealId) {
          game.closeReveal();
        }
        lastReason = "reading";
        return { type: "wait", reason: "reading" };
      }

      if (reactionCooldown > 0) {
        reactionCooldown = Math.max(0, reactionCooldown - dt);
        lastReason = "react";
        return { type: "wait", reason: "reaction lag" };
      }

      thinkAccum += dt;
      if (thinkAccum < THINK) {
        return { type: "wait", reason: "thinking" };
      }
      thinkAccum = 0;

      if (game.status !== "playing") {
        lastReason = "done";
        return { type: "wait", reason: "done" };
      }
      if (game.userPaused) {
        lastReason = "user pause";
        return { type: "wait", reason: "user pause" };
      }
      if (game.isProclaiming()) {
        lastReason = "proclaiming";
        return { type: "wait", reason: "proclaiming" };
      }

      // If Easy still has reveal open from prior, finish close
      if (game.pendingRevealId && !game.paused) {
        game.closeReveal();
      }

      const action =
        id === "hold-will"
          ? holdWillDecide(game)
          : id === "learner"
            ? learnerDecide(game)
            : id === "random"
              ? randomDecide(game)
              : { type: "wait" as const, reason: "unknown bot" };

      if (action.type === "wait") {
        lastReason = action.reason;
        return action;
      }

      if (action.type === "recall") {
        const ok = game.paidDraw();
        lastReason = ok ? action.reason : "recall failed";
        return ok ? action : { type: "wait", reason: "recall failed" };
      }

      if (action.type === "reveal") {
        const card = game.openReveal(action.instanceId);
        if (!card) {
          lastReason = "reveal failed";
          return { type: "wait", reason: "reveal failed" };
        }
        readCooldown = READ_TIME;
        lastReason = action.reason;
        return action;
      }

      if (action.type === "proclaim") {
        if (game.pendingRevealId) game.closeReveal();
        const ok = game.activate(action.instanceId, action.intent);
        lastReason = ok ? action.reason : `fail: ${action.reason}`;
        return ok ? action : { type: "wait", reason: lastReason };
      }

      lastReason = "noop";
      return action;
    },
  };
}

/** One-shot decide+apply without pacing (prefer createBotController for fair sims). */
export function runBotTick(game: Game, botId: BotId): BotAction | null {
  const c = createBotController(botId);
  // Fast-forward cooldowns for single-shot (UI legacy)
  return c.tick(game, 99);
}
